import { ApiError, apiGet, apiPost, apiDelete, postNdjsonStream } from "./client";
import type {
  Job,
  ScanHistory,
  ScanMeta,
  StreamEvent,
  ExploreScanSource,
} from "@career-ops-amplify/shared";
import type { ExploreResult } from "@/types";

export interface ScanFiltersRequest {
  titlePositive?: string[];
  titleNegative?: string[];
  locationAllow?: string[];
  locationBlock?: string[];
  seniorityBoost?: Record<string, number>;
  /** When set, hard-filter to titles matching these seniority chip labels. */
  seniorityRequire?: string[];
  companySlugs?: string[];
  includeBoards?: boolean;
  includeAts?: boolean;
  boardIds?: string[];
  includeSeen?: boolean;
}

export interface BoardScanSource {
  providerId: string;
  name: string;
  description?: string;
  enabled: boolean;
  selected: boolean;
  scannable: boolean;
  skipReason?: string;
}

export interface BoardScanSourcesResponse {
  boards: BoardScanSource[];
  total: number;
}

export interface ExploreOffer {
  url: string;
  company: string;
  role: string;
  location?: string;
  compensation?: string;
  source?: string;
}

export interface FreeScanResponse {
  results: ExploreResult[];
  count: number;
  meta?: ScanMeta;
  message?: string;
}

export interface ScanSourcesResponse {
  sources: ExploreScanSource[];
  total: number;
  scannableCount: number;
}

export interface AddOffersResponse {
  added: Job[];
  skipped: number;
  message?: string;
}

export type AiSearchEventCallback = (event: StreamEvent) => void;

/** List board aggregators for scan setup. */
export async function getBoardScanSources(): Promise<BoardScanSourcesResponse> {
  return apiGet<BoardScanSourcesResponse>("/api/explore/boards");
}

/** List scannable companies from the platform directory. */
export async function getScanSources(params?: {
  scannableOnly?: boolean;
  industry?: string;
  search?: string;
}): Promise<ScanSourcesResponse> {
  return apiGet<ScanSourcesResponse>("/api/explore/sources", {
    ...(params?.scannableOnly ? { scannableOnly: "true" } : {}),
    ...(params?.industry ? { industry: params.industry } : {}),
    ...(params?.search ? { search: params.search } : {}),
  });
}

/** Trigger ATS / board scan with optional company selection. */
export async function runFreeScan(
  filters?: ScanFiltersRequest,
): Promise<FreeScanResponse> {
  return apiPost<FreeScanResponse>("/api/explore", filters ?? {});
}

function isRetryableAiStatus(status: number): boolean {
  return status === 500 || status === 502 || status === 503 || status === 504;
}

/** Stream AI-powered job search (web search + grounded extraction). */
export async function runAiSearch(
  query: string,
  onEvent: AiSearchEventCallback,
  signal?: AbortSignal,
): Promise<{ tokens: number; costUsd: number | null } | null> {
  const runOnce = async (): Promise<{
    tokens: number;
    costUsd: number | null;
  } | null> => {
    let summary: { tokens: number; costUsd: number | null } | null = null;

    await postNdjsonStream(
      "/api/explore/ai",
      { query },
      (raw) => {
        const event = raw as StreamEvent;
        if (event.type === "done") {
          summary = { tokens: event.tokens, costUsd: event.costUsd ?? null };
        }
        onEvent(event);
      },
      signal,
    );

    return summary;
  };

  try {
    return await runOnce();
  } catch (err) {
    if (
      err instanceof ApiError &&
      isRetryableAiStatus(err.status) &&
      !signal?.aborted
    ) {
      return await runOnce();
    }
    throw err;
  }
}

/** Add discovered offers to the jobs inbox. */
export async function addDiscoveredOffers(
  offers: ExploreOffer[],
  source?: string,
): Promise<AddOffersResponse> {
  return apiPost<AddOffersResponse>("/api/explore/add", { offers, source });
}

/** Get seen URLs / scan history (cursor or offset pagination). */
export async function getScanHistory(params?: {
  limit?: number;
  offset?: number;
  cursor?: string;
  includeDismissed?: boolean;
}): Promise<{
  history: ScanHistory[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextCursor?: string;
}> {
  return apiGet("/api/explore/history", {
    limit: params?.limit,
    offset: params?.offset,
    cursor: params?.cursor,
    includeDismissed: params?.includeDismissed ? true : undefined,
  });
}

/** Hard-delete all seen URL history for the current user. */
export async function clearScanHistory(): Promise<{
  deleted: number;
  message?: string;
}> {
  return apiDelete("/api/explore/history");
}

/** Soft-dismiss one seen URL from the history panel. */
export async function dismissScanHistoryEntry(
  scanId: string,
): Promise<{ entry: ScanHistory }> {
  return apiPost(`/api/explore/history/${encodeURIComponent(scanId)}/dismiss`);
}

/** @deprecated Use runFreeScan instead */
export async function runExplore(
  portals?: string[],
): Promise<{ results: ExploreResult[]; inboxAdditions: Job[] }> {
  const res = await runFreeScan({ titlePositive: portals });
  return { results: res.results, inboxAdditions: [] };
}
